import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';
import CloudinaryImage from '@/components/ui/CloudinaryImage';

interface Article {
  id: string;
  slug?: string;
  title: string;
  excerpt: string;
  image: string;
  alt: string;
  author: string;
  publishedAt: string;
  category: string;
  readTime: number;
}

interface NewsSectionProps {
  articles: Article[];
}

const NewsSection = ({ articles }: NewsSectionProps) => {
  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Latest Mining News
            </h2>
            <p className="text-text-secondary">
              Stay informed with the latest updates from the mining industry
            </p>
          </div>
          <Link href="/news-hub">
            <button className="hidden md:flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-industrial hover:bg-accent transition-industrial font-heading font-bold uppercase tracking-wide">
              <span>View All News</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles?.map((article) => (
            <div
              key={article.id}
              className="bg-card border border-border rounded-industrial overflow-hidden industrial-shadow hover:shadow-lg transition-industrial"
            >
              <Link href={`/article-detail?slug=${article.slug}`}>
                <div className="relative h-48 overflow-hidden bg-muted">
                  <CloudinaryImage
                    src={article.image}
                    alt={article.alt}
                    fill
                    preset="card"
                    responsive
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
                      {article.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-heading font-bold text-card-foreground mb-3 line-clamp-2 hover:text-primary transition-industrial">
                    <Link href={`/article-detail?slug=${article.slug}`}>
                      {article.title}
                    </Link>
                  </h3>
                  
                  <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-text-secondary">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium">{article.author}</span>
                      <span>{article.publishedAt}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="ClockIcon" size={14} />
                      <span>{article.readTime} min read</span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Mobile View All Button */}
        <div className="mt-8 md:hidden">
          <Link href="/news-hub">
            <button className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-industrial hover:bg-accent transition-industrial font-heading font-bold uppercase tracking-wide">
              <span>View All News</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;